// =============================================================================
// safer-search.js — Predictive search with semantic synonym matching
// =============================================================================
// Drives <safer-search> custom elements (currently the mobile horizontal
// search bar). Uses Shopify's predictive-search API to fetch product/page
// suggestions, then re-ranks/expands the query with a synonym dictionary
// so that "pot light" → "downlight", "high bay" → "warehouse light", etc.
// resolve correctly even when the inventory uses the technical term.
//
// True semantic search (vector embeddings) requires a backend; this is
// "semantic-style" matching — synonyms + fuzzy + tag/type-aware ranking.
// =============================================================================

(function () {
  'use strict';

  /**
   * Synonym & alias dictionary for the LED + electrical inventory.
   * Keys = user-facing terms (what shoppers type).
   * Values = additional search terms that map to the same intent.
   *
   * Bidirectional: if user types any value, all the keys + other values
   * also become candidate matches. The dictionary is normalized to
   * lowercase at runtime.
   */
  const SYNONYM_GROUPS = [
    // LIGHT FIXTURES — common-name → technical-name
    ['pot light', 'pot lights', 'potlight', 'downlight', 'downlights', 'recessed light', 'recessed lights', 'can light', 'can lights', 'wafer light', 'slim panel'],
    ['high bay', 'highbay', 'warehouse light', 'warehouse lighting', 'industrial light', 'shop light', 'ufo light', 'ufo'],
    ['linear high bay', 'linear highbay', 'linear strip', 'linear light', '4ft strip', '4 foot strip'],
    ['wall pack', 'wallpack', 'building light', 'exterior light', 'outdoor wall light', 'cutoff', 'semi-cutoff', 'semi cutoff'],
    ['shoebox', 'shoe box', 'parking lot light', 'parking light', 'pole light', 'area light', 'street light'],
    ['flush mount', 'flushmount', 'ceiling light', 'ceiling fixture', 'surface mount'],
    ['vanity', 'vanity light', 'bathroom light', 'mirror light'],
    ['gimbal', 'gimbal light', 'adjustable downlight', 'tilt light', 'directional downlight'],
    ['fire rated', 'fire-rated', 'firerated', 'fire-resistance', 'ulc', 'ulc rated', '2hr rated'],
    ['wifi', 'wi-fi', 'smart', 'smart light', 'connected', 'app controlled', 'tuya', 'alexa', 'google home'],
    ['anti-glare', 'anti glare', 'antiglare', 'low glare', 'ugr'],
    ['cob', 'cob led', 'chip on board'],
    ['night light', 'nightlight', 'dim mode', '2000k', 'warm'],

    // ELECTRICAL HARDWARE
    ['conduit', 'pipe', 'pvc pipe', 'pvc conduit', 'sch 40', 'schedule 40'],
    ['lb', 'lb conduit', 'lb body', 'lb fitting', 'pvc lb', 'conduit body'],
    ['elbow', 'elbows', '90 elbow', '90 degree', 'corner fitting', 'sch40 elbow'],
    ['offset', 'off-set', 'off set'],
    ['electrical box', 'box', 'junction box', 'device box', 'outlet box', 'switch box'],
    ['octagon', 'octagon box', 'oct box', 'ceiling box', 'fixture box'],
    ['gang', 'single gang', '1g', 'one gang'],
    ['double gang', '2g', 'two gang', 'duplex box'],
    ['triple gang', '3g', 'three gang'],
    ['four gang', '4g', '4-gang', 'quad'],
    ['utility', 'utility box', 'handy box', 'surface box'],
    ['pan box', 'shallow box', 'pancake box'],
    ['fclip', 'f-clip', 'f clip', 'rework clip', 'old work clip'],
    ['iberville', 'ibervllle', '2104', '52151', '54151'],
    ['plate', 'cover plate', 'blank plate', 'cover'],
    ['extension ring', 'extension', 'depth extender'],
    ['weather guard', 'weatherguard', 'weather head', 'weatherhead', 'service head'],
    ['staple', 'staples', 'cable staple', 'wire staple', 'insulated staple', 'romex staple'],
    ['connector', 'cable connector', 'romex connector', 'nm connector', 'dlk'],
    ['photocell', 'photo cell', 'dusk to dawn', 'dusk-to-dawn', 'auto on'],
    ['bracket', 'mount', 'mounting bracket', 'yoke', 'arm', 'slip fitter'],

    // ATTRIBUTES
    ['cct', 'color temp', 'color temperature', 'kelvin', '5cct', '3cct', '5 cct', '3 cct'],
    ['watt', 'watts', 'wattage', 'power', 'w'],
    ['lumen', 'lumens', 'lm', 'brightness'],
    ['voltage', 'volts', 'volt', '120v', '347v', 'high voltage', 'commercial voltage'],
    ['ip65', 'ip 65', 'waterproof', 'weatherproof', 'outdoor rated'],
    ['dlc', 'dlc listed', 'dlc premium', 'rebate eligible', 'save on energy'],
    ['cul', 'cULus', 'csa', 'certified', 'listed', 'approved'],
    ['dimmable', 'dimming', '0-10v', 'dim', 'dimmer compatible'],
    ['selectable', 'tunable', 'switchable', 'multi-cct'],
  ];

  // Build a normalized lookup: term → Set of synonyms (including itself)
  const SYNONYM_INDEX = (() => {
    const idx = new Map();
    for (const group of SYNONYM_GROUPS) {
      const norm = group.map((g) => g.toLowerCase().trim());
      const set = new Set(norm);
      for (const term of norm) {
        if (idx.has(term)) {
          for (const s of set) idx.get(term).add(s);
        } else {
          idx.set(term, new Set(set));
        }
      }
    }
    return idx;
  })();

  /**
   * Expand a user query into candidate alternative queries via synonyms.
   * Returns an array of unique query strings, with the original first.
   */
  function expandQuery(raw) {
    const q = raw.toLowerCase().trim();
    if (!q) return [];
    const expansions = new Set([q]);
    for (const [term, group] of SYNONYM_INDEX) {
      if (q.includes(term)) {
        for (const synonym of group) {
          // Replace the matching token with each synonym
          const expanded = q.split(term).join(synonym);
          if (expanded !== q) expansions.add(expanded);
        }
      }
    }
    return Array.from(expansions);
  }

  /**
   * Score a product against the original query + its synonym expansions.
   * Higher = better match.
   *  - Exact title match = 100
   *  - Title prefix match = 50
   *  - Title contains query = 30
   *  - Title contains any synonym = 20
   *  - Tag/Type match = 15
   *  - Vendor match = 5
   */
  function scoreProduct(product, q, expansions) {
    const title = (product.title || '').toLowerCase();
    const tags = (product.tags || []).map((t) => String(t).toLowerCase());
    const productType = (product.product_type || '').toLowerCase();
    const vendor = (product.vendor || '').toLowerCase();
    let score = 0;
    if (title === q) score += 100;
    if (title.startsWith(q)) score += 50;
    if (title.includes(q)) score += 30;
    for (const e of expansions) {
      if (e !== q && title.includes(e)) score += 20;
      if (tags.some((t) => t.includes(e))) score += 15;
      if (productType.includes(e)) score += 12;
      if (vendor.includes(e)) score += 5;
    }
    // Boost in-stock items
    if (product.available !== false) score += 3;
    return score;
  }

  /**
   * Highlight the matched portion of a string for display.
   */
  function highlight(text, query) {
    if (!query) return escapeHTML(text);
    const safe = escapeHTML(text);
    const safeQ = escapeHTML(query).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return safe.replace(new RegExp(`(${safeQ})`, 'ig'), '<mark>$1</mark>');
  }

  function escapeHTML(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function safeUrl(u) {
    if (!u) return '#';
    if (u.startsWith('/') || u.startsWith(window.location.origin)) return u;
    try {
      const parsed = new URL(u, window.location.origin);
      return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : '#';
    } catch { return '#'; }
  }

  /**
   * Shopify's /search/suggest.json returns prices as dollar STRINGS
   * (e.g., "99.00"), not cents. The Admin GraphQL API returns cents.
   * Defensive: if the value is a string with a decimal, treat as dollars;
   * if it's a large integer (>= 1000 with no decimals) and we suspect
   * cents, divide by 100. Otherwise treat as dollars.
   */
  function formatPrice(value) {
    if (value == null || value === '') return '';
    const str = String(value);
    let num = Number(str);
    if (Number.isNaN(num)) return '';
    // If the source value had a decimal point, it's already in dollars.
    // Otherwise, only treat as cents if it's a clean integer ≥ 100 — and
    // even then, the predictive-search API doesn't do that. Stay in dollars.
    if (!str.includes('.') && num >= 100000) {
      // implausible CAD retail; assume cents
      num = num / 100;
    }
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
    }).format(num);
  }

  /**
   * Fetch one page of predictive-search results from Shopify.
   */
  async function fetchPredictive(query) {
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=8&resources[options][unavailable_products]=last`;
    const r = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error(`Search HTTP ${r.status}`);
    const data = await r.json();
    return data.resources?.results || {};
  }

  /**
   * Search using the original query + 1-2 strongest synonym expansions.
   * Combine + dedupe + re-rank by our scoring.
   */
  async function semanticSearch(rawQuery) {
    const expansions = expandQuery(rawQuery);
    // Try the original first, then up to 2 synonym variants for breadth
    const queries = [rawQuery, ...expansions.slice(1, 3)];
    const seenIds = new Set();
    const products = [];
    const collections = [];
    const pages = [];

    for (const q of queries) {
      try {
        const results = await fetchPredictive(q);
        for (const p of results.products || []) {
          if (seenIds.has(p.id)) continue;
          seenIds.add(p.id);
          products.push(p);
        }
        for (const c of results.collections || []) {
          if (!collections.find((x) => x.id === c.id)) collections.push(c);
        }
        for (const p of results.pages || []) {
          if (!pages.find((x) => x.id === p.id)) pages.push(p);
        }
      } catch (e) {
        // ignore individual failures
      }
    }

    const q = rawQuery.toLowerCase().trim();
    products.sort((a, b) => scoreProduct(b, q, expansions) - scoreProduct(a, q, expansions));

    return {
      products: products.slice(0, 8),
      collections: collections.slice(0, 3),
      pages: pages.slice(0, 2),
      expansions: expansions.slice(0, 4),
    };
  }

  // Expose the semantic engine so other components (e.g. the quick-add) can
  // reuse the synonym expansion without duplicating the dictionary.
  window.SaferSearch = window.SaferSearch || {};
  window.SaferSearch.expandQuery = expandQuery;
  window.SaferSearch.semanticSearch = semanticSearch;

  /**
   * Render the results dropdown.
   */
  function render(resultsEl, results, query) {
    const { products, collections, pages, expansions } = results;
    const totalHits = products.length + collections.length + pages.length;
    if (totalHits === 0) {
      resultsEl.innerHTML = `
        <div class="mobile-search-bar__empty">
          No matches for <strong>${escapeHTML(query)}</strong>.<br>
          <span style="opacity:0.65">Try: <em>pot light</em>, <em>high bay</em>, <em>2x4 panel</em>, <em>conduit</em>, <em>octagon box</em>.</span>
        </div>`;
      resultsEl.hidden = false;
      return;
    }

    let html = '';
    if (products.length) {
      html += `<div class="mobile-search-bar__group">
        <p class="mobile-search-bar__group-heading">Products · ${products.length}</p>`;
      for (const p of products) {
        const img = p.featured_image?.url || p.image || '';
        // Price-gated store: never render prices in search suggestions. The
        // /search/suggest.json endpoint exposes RETAIL, so showing it would
        // leak pricing to guests/bots. Pricing is by inquiry.
        const meta = [p.product_type].filter(Boolean).join(' · ');
        html += `
          <a class="mobile-search-bar__item" href="${safeUrl(p.url)}" role="option">
            ${img ? `<img class="mobile-search-bar__item-img" src="${escapeHTML(img)}" alt="" loading="lazy">` : '<div class="mobile-search-bar__item-img"></div>'}
            <div class="mobile-search-bar__item-text">
              <p class="mobile-search-bar__item-title">${highlight(p.title || '', query)}</p>
              ${meta ? `<p class="mobile-search-bar__item-meta">${escapeHTML(meta)}</p>` : ''}
            </div>
          </a>`;
      }
      html += `</div>`;
    }

    if (collections.length) {
      html += `<div class="mobile-search-bar__group">
        <p class="mobile-search-bar__group-heading">Categories · ${collections.length}</p>`;
      for (const c of collections) {
        html += `
          <a class="mobile-search-bar__item" href="${safeUrl(c.url)}" role="option">
            <div class="mobile-search-bar__item-img" aria-hidden="true" style="display:flex;align-items:center;justify-content:center;color:#1a3a60">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
            </div>
            <div class="mobile-search-bar__item-text">
              <p class="mobile-search-bar__item-title">${highlight(c.title || '', query)}</p>
              <p class="mobile-search-bar__item-meta">Browse collection</p>
            </div>
          </a>`;
      }
      html += `</div>`;
    }

    if (pages.length) {
      html += `<div class="mobile-search-bar__group">
        <p class="mobile-search-bar__group-heading">Pages · ${pages.length}</p>`;
      for (const pg of pages) {
        html += `
          <a class="mobile-search-bar__item" href="${safeUrl(pg.url)}" role="option">
            <div class="mobile-search-bar__item-img" aria-hidden="true" style="display:flex;align-items:center;justify-content:center;color:#1a3a60">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            </div>
            <div class="mobile-search-bar__item-text">
              <p class="mobile-search-bar__item-title">${highlight(pg.title || '', query)}</p>
              <p class="mobile-search-bar__item-meta">Page</p>
            </div>
          </a>`;
      }
      html += `</div>`;
    }

    html += `<a class="mobile-search-bar__view-all" href="/search?q=${encodeURIComponent(query)}">See all results →</a>`;

    if (expansions.length > 1) {
      const altText = expansions.slice(1, 4).map((e) => escapeHTML(e)).join(' · ');
      html = `<div class="mobile-search-bar__group">
        <p class="mobile-search-bar__group-heading" style="color:rgba(214,176,89,0.85)">Also showing matches for: ${altText}</p>
      </div>` + html;
    }

    resultsEl.innerHTML = html;
    resultsEl.hidden = false;
  }

  /**
   * The custom element.
   */
  class SaferSearch extends HTMLElement {
    constructor() {
      super();
      this.input = null;
      this.clearBtn = null;
      this.resultsEl = null;
      this.debounceTimer = null;
      this.lastQuery = '';
    }

    connectedCallback() {
      this.input = this.querySelector('[data-safer-search-input]');
      this.clearBtn = this.querySelector('[data-safer-search-clear]');
      this.resultsEl = this.querySelector('[data-safer-search-results]');
      if (!this.input || !this.resultsEl) return;

      this._onInput    = this.onInput.bind(this);
      this._onFocus    = this.onFocus.bind(this);
      this._onKey      = this.onKey.bind(this);
      this._onClear    = this.onClear.bind(this);
      this._onDocClick = this.onDocClick.bind(this);

      this.input.addEventListener('input', this._onInput);
      this.input.addEventListener('focus', this._onFocus);
      this.input.addEventListener('keydown', this._onKey);
      this.clearBtn?.addEventListener('click', this._onClear);
      document.addEventListener('click', this._onDocClick);
    }

    disconnectedCallback() {
      this.input?.removeEventListener('input', this._onInput);
      this.input?.removeEventListener('focus', this._onFocus);
      this.input?.removeEventListener('keydown', this._onKey);
      this.clearBtn?.removeEventListener('click', this._onClear);
      document.removeEventListener('click', this._onDocClick);
    }

    onInput(e) {
      const q = e.target.value;
      if (this.clearBtn) this.clearBtn.hidden = !q.length;
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      if (!q || q.trim().length < 2) {
        this.resultsEl.hidden = true;
        this.resultsEl.innerHTML = '';
        return;
      }
      this.debounceTimer = setTimeout(() => this.runSearch(q), 180);
    }

    onFocus() {
      const q = this.input.value;
      if (q && q.trim().length >= 2 && this.lastQuery === q && this.resultsEl.innerHTML) {
        this.resultsEl.hidden = false;
      }
    }

    onKey(e) {
      if (e.key === 'Escape') {
        this.resultsEl.hidden = true;
        this.input.blur();
      }
    }

    onClear() {
      this.input.value = '';
      this.clearBtn.hidden = true;
      this.resultsEl.hidden = true;
      this.resultsEl.innerHTML = '';
      this.input.focus();
    }

    onDocClick(e) {
      if (!this.contains(e.target)) {
        this.resultsEl.hidden = true;
      }
    }

    async runSearch(q) {
      this.lastQuery = q;
      try {
        const results = await semanticSearch(q);
        // race condition: ignore if user has changed the query in the meantime
        if (q !== this.lastQuery) return;
        render(this.resultsEl, results, q);
      } catch (e) {
        // soft-fail
        console.warn('SaferSearch:', e);
      }
    }
  }

  if (!customElements.get('safer-search')) {
    customElements.define('safer-search', SaferSearch);
  }
})();
